import React, { useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const PrivacyPolicy = () => {
  // Animation on scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate__animated", "animate__fadeInUp");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  const sections = [
    {
      id: "information-collection",
      title: "Information We Collect",
      icon: "bi-database",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">
            We collect various types of information to provide and improve our travel services:
          </p>

          <div className="bg-white bg-opacity-50 rounded-3 p-4 mt-3" style={{ border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <h5 className="fw-semibold text-dark mb-3">
              <i className="bi bi-person-fill text-gold me-2"></i>
              Personal Information You Provide:
            </h5>
            <ul className="list-unstyled">
              {[
                "Name, email address, phone number, and date of birth",
                "Government ID proof (Aadhar, Passport, Driver's License) for hotel check-in",
                "Payment information (credit/debit card details, UPI ID, wallet information)",
                "Booking preferences and special requests",
                "Travel history and past bookings",
                "Reviews, ratings, and feedback you provide",
                "Communication preferences and marketing choices"
              ].map((item, idx) => (
                <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                  <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                  <span className="text-secondary">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white bg-opacity-50 rounded-3 p-4" style={{ border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <h5 className="fw-semibold text-dark mb-3">
              <i className="bi bi-gear-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              Information Automatically Collected:
            </h5>
            <ul className="list-unstyled">
              {[
                "Device information (IP address, browser type, operating system)",
                "Usage data (pages visited, time spent, search queries)",
                "Location data (with your consent) for live tracking",
                "Cookies and similar tracking technologies",
                "Log files and error reports"
              ].map((item, idx) => (
                <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                  <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                  <span className="text-secondary">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "how-we-use",
      title: "How We Use Your Information",
      icon: "bi-eye",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">We use your information for the following purposes:</p>
          <div className="row g-3 mt-3">
            {[
              { icon: "bi-check-circle-fill", title: "Process Bookings", desc: "Confirm reservations, send booking confirmations, and manage cancellations" },
              { icon: "bi-check-circle-fill", title: "Payment Processing", desc: "Securely process payments and issue refunds" },
              { icon: "bi-check-circle-fill", title: "Customer Support", desc: "Respond to inquiries, resolve issues, and provide assistance" },
              { icon: "bi-check-circle-fill", title: "Personalization", desc: "Tailor recommendations and offers based on your preferences" },
              { icon: "bi-check-circle-fill", title: "Legal Compliance", desc: "Fulfill legal obligations and prevent fraudulent activities" },
              { icon: "bi-check-circle-fill", title: "Service Improvement", desc: "Analyze usage patterns to enhance our platform" }
            ].map((item, idx) => (
              <Col md={6} key={idx}>
                <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ background: "rgba(255, 215, 0, 0.05)" }}>
                  <i className={`${item.icon} text-gold mt-1 fs-5`} style={{ color: "#d4af37" }}></i>
                  <div>
                    <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                    <p className="text-secondary small mb-0">{item.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "location-tracking",
      title: "Live Location Tracking",
      icon: "bi-geo-alt",
      content: (
        <div className="space-y-4">
          <div className="bg-white bg-opacity-50 rounded-3 p-4" style={{ border: "1px solid rgba(212, 175, 55, 0.3)" }}>
            <h5 className="fw-semibold text-dark mb-3">
              <i className="bi bi-satellite text-gold me-2" style={{ color: "#d4af37" }}></i>
              Real-Time Location Services
            </h5>
            <p className="text-secondary">
              Our application collects and processes your location information to provide ride-related services 
              and improve the overall user experience for <strong>tour bookings, bus travel, flight connections, and hotel stays</strong>.
            </p>
          </div>

          <div className="row g-3">
            <Col md={6}>
              <div className="p-4 rounded-3 h-100" style={{ background: "rgba(212, 175, 55, 0.08)", border: "1px solid rgba(212, 175, 55, 0.2)" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="badge bg-gold text-white px-3 py-2">Foreground</span>
                  <i className="bi bi-phone text-gold fs-4" style={{ color: "#d4af37" }}></i>
                </div>
                <p className="text-secondary small">
                  We access your location while the app is in use to display your current position, 
                  navigate routes, calculate trip distances, and provide accurate pickup and drop-off information.
                </p>
              </div>
            </Col>
            <Col md={6}>
              <div className="p-4 rounded-3 h-100" style={{ background: "rgba(212, 175, 55, 0.08)", border: "1px solid rgba(212, 175, 55, 0.2)" }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="badge bg-gold text-white px-3 py-2">Background</span>
                  <i className="bi bi-phone-fill text-gold fs-4" style={{ color: "#d4af37" }}></i>
                </div>
                <p className="text-secondary small">
                  We access your location even when the app is running in the background to continuously track 
                  the driver's live location during an active trip. This ensures accurate trip progress, 
                  real-time location updates for customers, route optimization, and enhanced safety.
                </p>
              </div>
            </Col>
          </div>

          <div className="bg-white bg-opacity-50 rounded-3 p-4" style={{ border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <h6 className="fw-semibold text-dark mb-3">How We Use Your Location Data:</h6>
            <div className="row g-2">
              {[
                "Provide real-time driver tracking during active rides",
                "Share the driver's live location with customers",
                "Calculate routes, estimated arrival times (ETA), and trip distances",
                "Improve navigation accuracy and trip management",
                "Enhance the safety and reliability of our transportation services"
              ].map((item, idx) => (
                <Col md={6} key={idx}>
                  <div className="d-flex align-items-start gap-2">
                    <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                    <span className="text-secondary small">{item}</span>
                  </div>
                </Col>
              ))}
            </div>
          </div>

          <div className="bg-warning bg-opacity-10 rounded-3 p-4" style={{ border: "1px solid #d4af37" }}>
            <p className="text-dark small mb-0">
              <i className="bi bi-info-circle-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              <strong>User Control:</strong> You can manage or revoke location permissions at any time through your device settings. 
              Please note that disabling location access, especially background location, may limit or prevent certain ride-tracking 
              and navigation features from functioning correctly.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "information-sharing",
      title: "Information Sharing",
      icon: "bi-people",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">We share your information only in the following circumstances:</p>

          {[
            { title: "With Hotels and Service Providers:", desc: "Your name, contact details, ID proof, and booking preferences are shared with hotels to facilitate your stay." },
            { title: "Payment Partners:", desc: "Payment information is shared with secure payment gateways to process transactions." },
            { title: "Legal Requirements:", desc: "We may disclose information if required by law or to protect our rights and safety." }
          ].map((item, idx) => (
            <div key={idx} className="rounded-3 p-4" style={{ background: "rgba(212, 175, 55, 0.06)", borderLeft: "4px solid #d4af37" }}>
              <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
              <p className="text-secondary small mb-0">{item.desc}</p>
            </div>
          ))}

          <p className="text-secondary small fst-italic">We do not sell your personal information to third parties for marketing purposes.</p>
        </div>
      )
    },
    {
      id: "data-security",
      title: "Data Security",
      icon: "bi-shield-check",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">We implement robust security measures to protect your information:</p>

          <div className="row g-3">
            {[
              { icon: "bi-lock-fill", title: "Encryption", desc: "256-bit SSL encryption for all data transmission" },
              { icon: "bi-shield-fill-check", title: "Secure Storage", desc: "PCI DSS compliant servers for payment data" },
              { icon: "bi-clock-fill", title: "Regular Audits", desc: "Security assessments and vulnerability testing" }
            ].map((item, idx) => (
              <Col md={4} key={idx}>
                <div className="text-center p-4 rounded-3 h-100" style={{ background: "rgba(255, 215, 0, 0.05)" }}>
                  <div className="d-inline-flex align-items-center justify-content-center rounded-circle p-3 mb-3" style={{ background: "rgba(212, 175, 55, 0.15)" }}>
                    <i className={`${item.icon} fs-2`} style={{ color: "#d4af37" }}></i>
                  </div>
                  <h6 className="fw-semibold text-dark">{item.title}</h6>
                  <p className="text-secondary small mb-0">{item.desc}</p>
                </div>
              </Col>
            ))}
          </div>

          <div className="rounded-3 p-4" style={{ background: "rgba(13, 110, 253, 0.05)", border: "1px solid #d4af37" }}>
            <p className="text-dark small mb-0">
              <i className="bi bi-info-circle-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              <strong>Note:</strong> While we strive to protect your data, no method of transmission over the internet is 100% secure. 
              We encourage you to use strong passwords and keep your login credentials confidential.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "cookies",
      title: "Cookies & Tracking",
      icon: "bi-cookie",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">We use cookies and similar technologies to:</p>
          <ul className="list-unstyled">
            {[
              "Remember your preferences and login status",
              "Analyze website traffic and user behavior",
              "Personalize content and recommendations",
              "Serve relevant advertisements",
              "Process bookings and payments efficiently"
            ].map((item, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                <span className="text-secondary">{item}</span>
              </li>
            ))}
          </ul>

          <div className="bg-white bg-opacity-50 rounded-3 p-4" style={{ border: "1px solid rgba(212, 175, 55, 0.2)" }}>
            <h6 className="fw-semibold text-dark mb-3">Types of Cookies We Use:</h6>
            <div className="row g-3">
              {[
                { title: "Essential Cookies", desc: "Required for basic website functionality" },
                { title: "Analytics Cookies", desc: "Help us understand how visitors use our site" },
                { title: "Preference Cookies", desc: "Remember your settings and choices" },
                { title: "Marketing Cookies", desc: "Deliver relevant advertisements" }
              ].map((item, idx) => (
                <Col md={6} key={idx}>
                  <div className="p-3 rounded-3" style={{ background: "rgba(212, 175, 55, 0.05)" }}>
                    <p className="fw-semibold text-dark mb-1">{item.title}</p>
                    <p className="text-secondary small mb-0">{item.desc}</p>
                  </div>
                </Col>
              ))}
            </div>
          </div>

          <p className="text-secondary small">You can manage cookie preferences through your browser settings. However, disabling certain cookies may affect website functionality.</p>
        </div>
      )
    },
    {
      id: "your-rights",
      title: "Your Rights",
      icon: "bi-file-text",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">Under Indian law and global privacy regulations, you have the following rights:</p>

          <div className="row g-3">
            {[
              { icon: "bi-eye-fill", title: "Right to Access", desc: "Request a copy of your personal data" },
              { icon: "bi-pencil-fill", title: "Right to Rectification", desc: "Correct inaccurate or incomplete information" },
              { icon: "bi-trash-fill", title: "Right to Erasure", desc: "Request deletion of your data (subject to legal obligations)" },
              { icon: "bi-arrow-left-right", title: "Right to Data Portability", desc: "Receive your data in a structured format" },
              { icon: "bi-x-circle-fill", title: "Right to Withdraw Consent", desc: "Opt out of marketing communications" }
            ].map((item, idx) => (
              <Col md={6} key={idx}>
                <div className="d-flex align-items-start gap-3 p-3 rounded-3" style={{ background: "rgba(212, 175, 55, 0.05)" }}>
                  <i className={`${item.icon} text-gold mt-1`} style={{ color: "#d4af37" }}></i>
                  <div>
                    <h6 className="fw-semibold text-dark mb-1">{item.title}</h6>
                    <p className="text-secondary small mb-0">{item.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </div>

          <div className="rounded-3 p-4" style={{ background: "rgba(212, 175, 55, 0.08)", border: "1px solid #d4af37" }}>
            <p className="text-dark small mb-0">
              <i className="bi bi-envelope-fill text-gold me-2" style={{ color: "#d4af37" }}></i>
              To exercise these rights, contact us at <strong className="text-gold" style={{ color: "#d4af37" }}>info@jayrajgroup.org</strong>
            </p>
          </div>
        </div>
      )
    },
    {
      id: "data-retention",
      title: "Data Retention",
      icon: "bi-clock",
      content: (
        <div className="space-y-4">
          <p className="text-secondary">We retain your personal information for as long as necessary to:</p>
          <ul className="list-unstyled">
            {[
              "Provide our services and fulfill bookings",
              "Comply with legal and regulatory requirements",
              "Resolve disputes and enforce agreements",
              "Improve our services and user experience"
            ].map((item, idx) => (
              <li key={idx} className="d-flex align-items-start gap-2 mb-2">
                <i className="bi bi-check-circle-fill text-gold mt-1" style={{ color: "#d4af37" }}></i>
                <span className="text-secondary">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-secondary small">Typically, booking data is retained for 7 years to comply with tax and accounting regulations. Marketing preferences are retained until you opt out.</p>
        </div>
      )
    }
  ];

  return (
    <div className="privacy-policy-section" style={{ 
      background: "linear-gradient(135deg, #faf8f5, #f5f0e8)",
      minHeight: "100vh"
    }}>
      {/* Gold Accent Line */}
      <div style={{ height: "4px", background: "linear-gradient(90deg, #d4af37, #f5d37c, #d4af37)" }}></div>

      {/* Hero Section with Gold Theme */}
      <div className="py-5" style={{ 
        background: "linear-gradient(135deg, #1a1a1a, #2d2d2d)",
        position: "relative",
        overflow: "hidden"
      }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <div className="mb-4 animate__animated animate__fadeInDown">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle p-3 mb-3" style={{ 
                  background: "rgba(212, 175, 55, 0.15)",
                  border: "2px solid #d4af37"
                }}>
                  <i className="bi bi-shield-check fs-1" style={{ color: "#d4af37" }}></i>
                </div>
                <h1 className="display-4 fw-bold text-white">
                  <span style={{ color: "#d4af37" }}>Privacy</span> Policy
                </h1>
                <p className="text-light opacity-75 fs-5 mt-3">
                  Your privacy is paramount to us. Learn how we collect, use, and protect your information 
                  across our travel services.
                </p>
                <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
                  <span className="badge px-4 py-2" style={{ background: "rgba(212, 175, 55, 0.2)", color: "#d4af37" }}>
                    <i className="bi bi-calendar3 me-2"></i> Last Updated: May 2026
                  </span>
                  <span className="badge px-4 py-2" style={{ background: "rgba(212, 175, 55, 0.2)", color: "#d4af37" }}>
                    <i className="bi bi-clock me-2"></i> Effective Immediately
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        {/* Decorative Elements */}
        <div style={{
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "300px",
          height: "300px",
          borderRadius: "50%",
          background: "rgba(212, 175, 55, 0.03)",
          pointerEvents: "none"
        }}></div>
        <div style={{
          position: "absolute",
          bottom: "-30%",
          left: "-5%",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          background: "rgba(212, 175, 55, 0.02)",
          pointerEvents: "none"
        }}></div>
      </div>

      <Container className="py-5">
        <Row>
          {/* Sidebar Navigation */}
          <Col lg={3} className="mb-4">
            <div className="position-sticky" style={{ top: "2rem" }}>
              <Card className="border-0 shadow-lg rounded-4" style={{ background: "rgba(255, 255, 255, 0.9)", backdropFilter: "blur(8px)" }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold text-dark mb-3" style={{ borderBottom: "2px solid #d4af37", paddingBottom: "0.75rem" }}>
                    <i className="bi bi-list-ul me-2" style={{ color: "#d4af37" }}></i>
                    Quick Links
                  </h5>
                  <nav className="d-flex flex-column gap-1">
                    {sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="text-decoration-none py-2 px-3 rounded-3 transition-all"
                        style={{ 
                          color: "#4a4a4a",
                          fontSize: "0.9rem",
                          transition: "all 0.3s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "rgba(212, 175, 55, 0.1)";
                          e.target.style.color = "#d4af37";
                          e.target.style.paddingLeft = "1.25rem";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "transparent";
                          e.target.style.color = "#4a4a4a";
                          e.target.style.paddingLeft = "1rem";
                        }}
                      >
                        <i className={`${section.icon} me-2`} style={{ color: "#d4af37", width: "18px" }}></i>
                        {section.title}
                      </a>
                    ))}
                  </nav>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Main Content */}
          <Col lg={9}>
            {/* Introduction */}
            <Card className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll" style={{ background: "rgba(255, 255, 255, 0.85)", backdropFilter: "blur(8px)" }}>
              <Card.Body className="p-4 p-lg-5">
                <h2 className="fw-bold text-dark mb-3">
                  <span style={{ color: "#d4af37" }}>Introduction</span>
                </h2>
                <div className="text-secondary" style={{ lineHeight: "1.8" }}>
                  <p>
                    Welcome to <strong className="text-dark">Jayraj Group</strong> ("we," "our," or "us"). This Privacy Policy explains 
                    how we collect, use, disclose, and safeguard your information when you use our website, mobile application, 
                    and services to book <strong className="text-dark">tours, buses, flights, and hotels</strong> across India.
                  </p>
                  <p>
                    By accessing or using our platform, you consent to the practices described in this policy. 
                    If you do not agree with this policy, please do not use our services.
                  </p>
                  <div className="rounded-3 p-4 mt-3" style={{ background: "rgba(212, 175, 55, 0.08)", borderLeft: "4px solid #d4af37" }}>
                    <p className="text-dark small mb-0">
                      <i className="bi bi-info-circle-fill me-2" style={{ color: "#d4af37" }}></i>
                      <strong>Information Officer:</strong> For privacy-related queries, contact our Grievance Officer at 
                      <strong className="text-gold" style={{ color: "#d4af37" }}> info@jayrajgroup.org</strong> or call 
                      <strong className="text-gold" style={{ color: "#d4af37" }}> +91-8869872377</strong>
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Policy Sections */}
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <Card 
                  key={section.id} 
                  id={section.id} 
                  className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll"
                  style={{ 
                    background: "rgba(255, 255, 255, 0.85)", 
                    backdropFilter: "blur(8px)",
                    transition: "all 0.3s ease",
                    scrollMarginTop: "2rem"
                  }}
                >
                  <Card.Body className="p-4 p-lg-5">
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-3" style={{ 
                        background: "rgba(212, 175, 55, 0.12)",
                        border: "1px solid rgba(212, 175, 55, 0.3)"
                      }}>
                        <i className={`${Icon} fs-4`} style={{ color: "#d4af37" }}></i>
                      </div>
                      <h2 className="fw-bold text-dark mb-0" style={{ fontSize: "1.5rem" }}>
                        {section.title}
                      </h2>
                    </div>
                    <div style={{ color: "#4a4a4a" }}>
                      {section.content}
                    </div>
                  </Card.Body>
                </Card>
              );
            })}

            {/* Contact Section */}
            <Card className="border-0 shadow-lg rounded-4 mb-4 animate-on-scroll" style={{ 
              background: "linear-gradient(135deg, rgba(26, 26, 26, 0.95), rgba(45, 45, 45, 0.95))",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(212, 175, 55, 0.3)"
            }}>
              <Card.Body className="p-4 p-lg-5">
                <h2 className="fw-bold text-white mb-4">
                  <span style={{ color: "#d4af37" }}>Contact</span> Us
                </h2>
                <Row className="g-4">
                  <Col md={6}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-envelope-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Email</p>
                        <p className="text-white mb-0">support@jayrajgroup.org</p>
                        <p className="text-white mb-0">info@jayrajgroup.org</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-telephone-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Phone</p>
                        <p className="text-white mb-0">+91-8869872377</p>
                        <p className="text-white mb-0">+91-8869872377 (24/7 Support)</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-geo-alt-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Address</p>
                        <p className="text-white mb-0">Jayraj Group, Semitalli, Kedarnath Main Road,</p>
                        <p className="text-white mb-0">Guptkashi, Dist. Rudraprayag,</p>
                        <p className="text-white mb-0">Uttarakhand – 246439</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="d-flex align-items-center justify-content-center rounded-circle p-2" style={{ 
                        background: "rgba(212, 175, 55, 0.15)",
                        minWidth: "40px",
                        minHeight: "40px"
                      }}>
                        <i className="bi bi-clock-fill" style={{ color: "#d4af37" }}></i>
                      </div>
                      <div>
                        <p className="text-light-50 small mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>Grievance Redressal</p>
                        <p className="text-white mb-0">Mon-Fri: 10:00 AM - 6:00 PM IST</p>
                        <p className="text-white mb-0">Response within 24-48 hours</p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Footer */}
            <div className="text-center py-4">
              <p className="text-secondary small mb-0">
                <i className="bi bi-shield-lock me-1" style={{ color: "#d4af37" }}></i>
                This Privacy Policy is governed by the laws of India and the Information Technology Act, 2000.
              </p>
              <p className="text-secondary small mt-2">
                © {new Date().getFullYear()} Jayraj Group. All rights reserved.
              </p>
              <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-bus-front me-1"></i> Tours
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-airplane me-1"></i> Flights
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-building-hotel me-1"></i> Hotels
                </span>
                <span className="badge" style={{ background: "rgba(212, 175, 55, 0.1)", color: "#d4af37" }}>
                  <i className="bi bi-geo-alt-fill me-1"></i> Live Tracking
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .text-gold {
          color: #d4af37;
        }
        .bg-gold {
          background: #d4af37;
        }
        .animate-on-scroll {
          opacity: 0;
          transform: translateY(30px);
          animation-fill-mode: forwards;
        }
        .animate__fadeInUp {
          opacity: 1;
          transform: translateY(0);
        }
        .transition-all {
          transition: all 0.3s ease;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.6);
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;